import { useMsal } from '@azure/msal-react'
import {
  Box,
  Button,
  Group,
  Image,
  Loader,
  MultiSelect,
  Progress,
  Stack,
  Text,
  TextInput,
  Title,
  UnstyledButton,
} from '@mantine/core'
import { useLocalStorage } from '@mantine/hooks'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Logo from '../assets/HiddenGemzLogo.png'
import { useSessionInfo } from '../contexts/SessionContext'
import {
  fetchOnboardingOrganizationById,
  updateOrganization,
} from '../data/organization.service'
import { CategoryDto, fetchCategories } from '../data/location.service'
import {
  defaultOrganizationOnboardingState,
  OnboardingOrganization,
} from '../models/Onboarding/OnboardingOrganization'
import { OnboardingSteps } from '../utils/constants'
import { getCurrentStepFromAdvertiserOnboardingState } from '../utils/onboardingUtils'
import classes from './css/Onboarding.module.css'

function Onboarding() {
  const { session, setSession } = useSessionInfo()
  const organizationId = session?.organizationId
  const [currentStep, setCurrentStep] = useState(
    OnboardingSteps.OrganizationName,
  )
  const [organization, setOrganization] = useState<OnboardingOrganization>(
    defaultOrganizationOnboardingState,
  )
  const [isLoadingOnboardingState, setIsLoadingOnboardingState] =
    useState(false)
  const [isLoadingNextStep, setIsLoadingNextStep] = useState(false)
  const [isLoadingBackStep, setIsLoadingBackStep] = useState(false)
  const [needsChangesSaved, setNeedsChangesSaved] = useState(false)
  const [categories, setCategories] = useState<CategoryDto[]>([])
  const navigate = useNavigate()
  const { instance } = useMsal()

  const [, , removeValue] = useLocalStorage({
    key: 'isHostMode',
    defaultValue: false,
  })

  useEffect(() => {
    const getOnboardingState = async (organizationId: number | undefined) => {
      if (!isLoadingOnboardingState && organizationId != null) {
        setIsLoadingOnboardingState(true)
        try {
          const organization = await fetchOnboardingOrganizationById(
            organizationId,
            instance,
          )
          setOrganization(organization)
          const currentStep =
            getCurrentStepFromAdvertiserOnboardingState(organization)
          setCurrentStep(currentStep)
        } catch (ex: any) {
          notifications.show({
            title: 'An unknown error occurred',
            message:
              'Unable to get organization info. Refresh page. If you are unable to resolve the issue, please contact us for help',
            autoClose: false,
          })
          console.log(ex)
        } finally {
          setIsLoadingOnboardingState(false)
        }
      }
    }

    const loadCategories = async () => {
      try {
        const fetchedCategories = await fetchCategories()
        setCategories(fetchedCategories)
      } catch (error) {
        console.error('Error fetching categories:', error)
      }
    }

    getOnboardingState(organizationId)
    loadCategories()
  }, [organizationId, instance])

  const saveChanges = async () => {
    if (!needsChangesSaved) return

    const organizationToUpdate: Partial<OnboardingOrganization> = {
      id: organizationId,
    }
    switch (currentStep) {
      case OnboardingSteps.OrganizationName:
        organizationToUpdate.name = organization.name
        break
      case OnboardingSteps.Category:
        organizationToUpdate.categoryIds = organization.categoryIds
        break
    }

    await updateOrganization(organizationToUpdate, instance)
    setNeedsChangesSaved(false)
  }

  const handleNextStep = async () => {
    setIsLoadingNextStep(true)
    await saveChanges()
    checkForLastStep()
    setCurrentStep((prev) => prev + 1)
    setIsLoadingNextStep(false)
  }

  const handleGoBack = async () => {
    setIsLoadingBackStep(true)
    await saveChanges()
    setCurrentStep((prev) => prev - 1)
    setIsLoadingBackStep(false)
  }

  const onFieldChange = (state: Partial<OnboardingOrganization>) => {
    setNeedsChangesSaved(true)
    setOrganization((prev) => ({
      ...prev,
      ...state,
    }))
  }

  const checkForLastStep = () => {
    if (currentStep === OnboardingSteps.Category) {
      if (session) {
        setSession({
          ...session,
          isAdvertiserOnboardingComplete: true,
        })
      }
      modals.openConfirmModal({
        styles: { title: { fontWeight: 'bold', fontSize: 20 } },
        title: 'Welcome to Hidden Gemz!',
        centered: true,
        children: (
          <Stack>
            <Text>
              We're so glad to have you as an advertiser! Select a location and
              upload your first ad to get started or complete your business
              profile to become a host.
            </Text>
          </Stack>
        ),
        labels: { confirm: 'Start Advertising', cancel: 'Become a Host' },
        onCancel: () => {
          navigate('hostOnboarding')
        },
      })
      navigate('/')
    }
  }

  const isCurrentStepValid = (currentStep: number) => {
    switch (currentStep) {
      case OnboardingSteps.OrganizationName:
        return !!organization.name
      case OnboardingSteps.Category:
        return organization.categoryIds.length > 0
      default:
        break
    }
    return true
  }

  const getTitleTextForCurrentStep = () => {
    switch (currentStep) {
      case OnboardingSteps.OrganizationName:
        return 'What is the name of your business?'
      case OnboardingSteps.Category:
        return 'Which of these categories describe your business?'
      default:
        break
    }
    return ''
  }

  const handleSignOutClick = () => {
    setSession(null)
    removeValue()
    instance.logout().catch((error) => console.log(error))
  }

  const handleCheckForEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleNextStep()
    }
  }

  return (
    <Stack h='100%' w='100%'>
      <Group justify='space-between' mr={60} mt={24} ml={60}>
        <UnstyledButton
          onClick={() => {
            navigate('/')
          }}
        >
          <Image src={Logo} style={{ width: 100 }} />
        </UnstyledButton>
        <Button onClick={handleSignOutClick}>Sign Out</Button>
      </Group>
      {isLoadingOnboardingState ? (
        <Stack h='100%' justify='center' align='center'>
          <Loader />
        </Stack>
      ) : (
        <Stack h={'100%'} align='center' justify='center' flex={8} p='lg'>
          <Stack align='center' pb='lg'>
            <Title ta='center'>{getTitleTextForCurrentStep()}</Title>
          </Stack>
          {currentStep === OnboardingSteps.OrganizationName && (
            <TextInput
              placeholder='Hidden Gemz'
              value={organization.name ?? ''}
              onChange={(e) => onFieldChange({ name: e.target.value })}
              w='80%'
              maw={400}
              onKeyDown={handleCheckForEnter}
            />
          )}

          {currentStep === OnboardingSteps.Category && (
            <MultiSelect
              w='80%'
              maw={600}
              placeholder='Select all that apply'
              data={categories.map((c) => ({
                label: c.name,
                value: c.id.toString(),
              }))}
              value={organization.categoryIds.map((i) => i.toString())}
              onChange={(value) =>
                onFieldChange({ categoryIds: value.map((id) => parseInt(id)) })
              }
              searchable
              clearable
            />
          )}
        </Stack>
      )}

      <Stack
        w='100%'
        pos='sticky'
        style={{
          bottom: 0,
          borderTop: '1px solid lightgray',
        }}
        bg='white'
        pt={30}
      >
        <Group w={'100%'} pl={60} pr={60} align='center'>
          <Progress flex={1} value={((currentStep + 1) / 2) * 100} />
        </Group>
        <Group pt={0} p={20} pl={60} pr={60} justify='space-between'>
          <UnstyledButton
            onClick={handleGoBack}
            className={classes.navigationButton}
            style={{
              visibility:
                currentStep !== OnboardingSteps.OrganizationName
                  ? 'visible'
                  : 'hidden',
              height: 40,
              alignContent: 'center',
            }}
            disabled={isLoadingNextStep || isLoadingBackStep}
          >
            <Box h='100%' display='flex' style={{ alignItems: 'center' }}>
              {isLoadingBackStep ? (
                <Loader type='dots' color='black' size='sm' />
              ) : (
                'Back'
              )}
            </Box>
          </UnstyledButton>

          <Button
            onClick={handleNextStep}
            disabled={
              !isCurrentStepValid(currentStep) ||
              isLoadingNextStep ||
              isLoadingBackStep
            }
            ta='center'
            size='md'
          >
            {isLoadingNextStep ? (
              <Loader type='dots' size='md' />
            ) : currentStep === OnboardingSteps.Category ? (
              'Done'
            ) : (
              'Next'
            )}
          </Button>
        </Group>
      </Stack>
    </Stack>
  )
}

export default Onboarding
